import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, FileText, Upload, Clock, Sparkles, Loader2, ArrowRight, Check, AlertTriangle, FileUp } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Import a client (and optional past sessions) from raw intake text, an
 * intake file (PDF/JPG/PNG), or a pasted block of historical notes.
 *
 * The flow is always:
 *   1) Therapist picks a source
 *   2) We call the AI extractor → receive a DRAFT (nothing saved yet)
 *   3) Therapist reviews & edits inline
 *   4) Therapist clicks "Create client" / "Create client + N sessions"
 *      → we call the same endpoint with commit=true to write it
 */

const TABS = [
  { id: "text", label: "Paste intake", Icon: FileText, blurb: "Paste a typed intake form. AI extracts contact, history, complaints." },
  { id: "file", label: "Upload intake", Icon: Upload, blurb: "Upload an intake PDF or photo. Claude Vision reads the page." },
  { id: "history", label: "Paste full history", Icon: Clock, blurb: "Paste months of notes / SOAPs. AI rebuilds the client + a session timeline." },
  { id: "jotform", label: "Jotform", Icon: FileUp, blurb: "Connected Jotform inbox — auto-imports + auto-matches new submissions." },
];

export default function ImportClientModal({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("text");
  const [busy, setBusy] = useState(false);

  // Paste intake
  const [text, setText] = useState("");

  // Upload file
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);

  // Paste history
  const [historyText, setHistoryText] = useState("");
  const [nameHint, setNameHint] = useState("");

  // Shared draft state (text + file paths both populate this)
  const [draft, setDraft] = useState(null);
  const [draftMeta, setDraftMeta] = useState(null); // { source: 'text'|'file'|'history', extraction_notes, confidence, frequently_treated_areas, progress_summary }
  const [sessionDrafts, setSessionDrafts] = useState([]);
  const [errorBanner, setErrorBanner] = useState("");

  const resetAll = () => {
    setText("");
    setHistoryText("");
    setNameHint("");
    setFile(null);
    setDraft(null);
    setDraftMeta(null);
    setSessionDrafts([]);
    setErrorBanner("");
  };

  const close = () => { resetAll(); onClose(); };

  // ---------- Step 1 → 2: extraction ----------
  const runTextExtract = async () => {
    if (!text.trim()) { toast.error("Paste the intake text first."); return; }
    setBusy(true); setErrorBanner("");
    try {
      const r = await api.post("/intake/extract-text", { text: text.trim() });
      setDraft(r.data.client_draft);
      setDraftMeta({ source: "text" });
    } catch (e) {
      setErrorBanner(e?.response?.data?.detail || "Extraction failed. Try again or paste cleaner text.");
    } finally { setBusy(false); }
  };

  const runFileExtract = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    setBusy(true); setErrorBanner("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit", "false");
      const r = await api.post("/intake/extract-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft(r.data.client_draft);
      setDraftMeta({ source: "file" });
    } catch (e) {
      setErrorBanner(e?.response?.data?.detail || "Could not read the file. Try a clearer scan.");
    } finally { setBusy(false); }
  };

  const runHistoryExtract = async () => {
    if (!historyText.trim()) { toast.error("Paste the history first."); return; }
    setBusy(true); setErrorBanner("");
    try {
      const r = await api.post("/intake/historical-import", {
        text: historyText.trim(),
        commit: false,
        name_hint: nameHint.trim() || null,
      });
      setDraft(r.data.client_draft);
      setSessionDrafts(r.data.session_drafts || []);
      setDraftMeta({
        source: "history",
        extraction_notes: r.data.extraction_notes,
        confidence: r.data.confidence,
        frequently_treated_areas: r.data.frequently_treated_areas,
        progress_summary: r.data.progress_summary,
      });
    } catch (e) {
      setErrorBanner(e?.response?.data?.detail || "Could not reconstruct the history. Try splitting the paste into smaller chunks.");
    } finally { setBusy(false); }
  };

  // ---------- Step 4: commit ----------
  const commit = async () => {
    if (!draft) return;
    setBusy(true); setErrorBanner("");
    try {
      const isHistory = draftMeta?.source === "history";
      let resp;
      if (isHistory) {
        resp = await api.post("/intake/historical-import", {
          text: historyText.trim(),
          commit: true,
          name_hint: nameHint.trim() || null,
        });
      } else if (draftMeta?.source === "file") {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("commit", "true");
        resp = await api.post("/intake/extract-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        resp = await api.post("/intake/extract-text", { text: text.trim(), commit: true });
      }
      const created = resp.data.client || resp.data.client_draft;
      toast.success(isHistory && (resp.data.sessions?.length || 0) > 0
        ? `Client created with ${resp.data.sessions.length} session${resp.data.sessions.length === 1 ? "" : "s"}.`
        : "Client created.");
      if (onCreated) onCreated(created);
      close();
      if (created?.id) navigate(`/clients/${created.id}`);
    } catch (e) {
      setErrorBanner(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  // ---------- Inline edit on draft ----------
  const setDraftField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setDraftListAt = (k, i, v) => setDraft((d) => {
    const arr = [...(d[k] || [])]; arr[i] = v; return { ...d, [k]: arr };
  });
  const removeDraftListAt = (k, i) => setDraft((d) => {
    const arr = [...(d[k] || [])]; arr.splice(i, 1); return { ...d, [k]: arr };
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4" data-testid="import-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] flex flex-col relative animate-fade-up shadow-xl">
        <button
          data-testid="import-close-btn"
          onClick={close}
          className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center z-10"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        {/* Header */}
        <div className="px-6 sm:px-8 pt-7 pb-4 border-b border-paper-200">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-sage-600" />
            <div className="label-overline">Import a client</div>
          </div>
          <h2 className="font-heading text-2xl font-medium text-ink-900">Bring an existing client in fast</h2>
          <p className="text-sm text-ink-700 mt-1">
            Paste an intake form, upload a scan, or paste a long block of history — Posturra extracts the structured fields and shows you a draft to review before saving.
          </p>
        </div>

        {/* Tabs */}
        <div className="px-6 sm:px-8 pt-4 border-b border-paper-200">
          <div className="flex flex-wrap gap-2" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                data-testid={`import-tab-${t.id}`}
                onClick={() => { setTab(t.id); setDraft(null); setDraftMeta(null); setSessionDrafts([]); setErrorBanner(""); }}
                className={`min-h-[40px] px-4 rounded-full text-sm font-medium flex items-center gap-2 transition-all border ${
                  tab === t.id
                    ? "bg-ink-900 text-white border-ink-900"
                    : "bg-white text-ink-700 border-paper-200 hover:border-ink-900/30"
                }`}
              >
                <t.Icon className="w-3.5 h-3.5" strokeWidth={2} />
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-ink-500 mt-3 mb-4">{TABS.find((t) => t.id === tab)?.blurb}</p>
        </div>

        {/* Body — either input UI or draft review UI */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5">
          {errorBanner && (
            <div className="mb-4 rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900 flex gap-3" data-testid="import-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
              <div>{errorBanner}</div>
            </div>
          )}

          {!draft && tab === "text" && (
            <TextTab text={text} setText={setText} />
          )}
          {!draft && tab === "file" && (
            <FileTab file={file} setFile={setFile} fileInputRef={fileInputRef} />
          )}
          {!draft && tab === "history" && (
            <HistoryTab text={historyText} setText={setHistoryText} nameHint={nameHint} setNameHint={setNameHint} />
          )}
          {tab === "jotform" && (
            <JotformTab onClose={close} />
          )}

          {draft && (
            <DraftReview
              draft={draft}
              meta={draftMeta}
              sessionDrafts={sessionDrafts}
              setDraftField={setDraftField}
              setDraftListAt={setDraftListAt}
              removeDraftListAt={removeDraftListAt}
            />
          )}
        </div>

        {/* Footer actions */}
        {tab !== "jotform" && (
          <div className="px-6 sm:px-8 py-4 border-t border-paper-200 bg-paper-50/40 rounded-b-3xl flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
            {!draft ? (
              <>
                <p className="text-xs text-ink-500">AI extraction · 1 workflow used · review the draft before saving.</p>
                {tab === "text" && (
                  <button
                    data-testid="import-extract-text-btn"
                    onClick={runTextExtract}
                    disabled={busy || !text.trim()}
                    className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {busy ? "Extracting…" : "Extract intake"}
                  </button>
                )}
                {tab === "file" && (
                  <button
                    data-testid="import-extract-file-btn"
                    onClick={runFileExtract}
                    disabled={busy || !file}
                    className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {busy ? "Reading…" : "Read & extract"}
                  </button>
                )}
                {tab === "history" && (
                  <button
                    data-testid="import-extract-history-btn"
                    onClick={runHistoryExtract}
                    disabled={busy || !historyText.trim()}
                    className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {busy ? "Reconstructing…" : "Reconstruct history"}
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  data-testid="import-back-btn"
                  onClick={() => { setDraft(null); setSessionDrafts([]); setDraftMeta(null); }}
                  className="min-h-[44px] px-4 rounded-full text-ink-700 hover:bg-paper-100 text-sm font-medium"
                >
                  ← Edit source
                </button>
                <button
                  data-testid="import-commit-btn"
                  onClick={commit}
                  disabled={busy || !draft?.name}
                  className="min-h-[48px] px-6 rounded-full bg-ink-900 hover:bg-ink-700 text-white font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {busy
                    ? "Saving…"
                    : sessionDrafts.length > 0
                      ? `Create client + ${sessionDrafts.length} session${sessionDrafts.length === 1 ? "" : "s"}`
                      : "Create client"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Tab content components ----------

function TextTab({ text, setText }) {
  return (
    <div>
      <label className="label-overline block mb-2">Paste the intake form text</label>
      <textarea
        data-testid="import-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={14}
        className="w-full px-4 py-3 rounded-2xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-y text-sm leading-relaxed"
        placeholder={"Name: Sarah Chen\nDOB: 01/14/1981\nPhone: (555) 123-4567\nEmail: sarah@example.com\nOccupation: software engineer\n\nChief complaint: chronic upper back tension, headaches 2-3x/wk\nMedical history: L4-L5 herniation 2019, ACL repair 2012\nSensitivities: lavender, deep pressure on neck\n..."}
      />
      <p className="text-xs text-ink-500 mt-2">Free-form is fine — bullet points, prose, or copied form fields all work.</p>
    </div>
  );
}

function FileTab({ file, setFile, fileInputRef }) {
  return (
    <div>
      <label className="label-overline block mb-2">Upload an intake form</label>
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) setFile(f);
        }}
        className="w-full min-h-[180px] rounded-2xl bg-paper-50 border-2 border-dashed border-paper-300 hover:border-sage-500 hover:bg-sage-50/40 transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer p-6 text-center"
        data-testid="import-file-dropzone"
      >
        <Upload className="w-7 h-7 text-ink-500" strokeWidth={1.5} />
        {file ? (
          <>
            <div className="font-medium text-ink-900">{file.name}</div>
            <div className="text-xs text-ink-500">{(file.size / 1024).toFixed(1)} KB · click to replace</div>
          </>
        ) : (
          <>
            <div className="font-medium text-ink-900">Drop a file or click to choose</div>
            <div className="text-xs text-ink-500">PDF, JPG, PNG, WEBP, HEIC · max 5 MB</div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          data-testid="import-file-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </div>
      <p className="text-xs text-ink-500 mt-2">Claude Vision reads the page directly — no OCR setup needed.</p>
    </div>
  );
}

function HistoryTab({ text, setText, nameHint, setNameHint }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="label-overline block mb-2">Client name (helps when notes are ambiguous)</label>
        <input
          data-testid="import-history-name-hint"
          value={nameHint}
          onChange={(e) => setNameHint(e.target.value)}
          className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
          placeholder="Sarah Chen"
        />
      </div>
      <div>
        <label className="label-overline block mb-2">Paste full history (intake + SOAPs + freeform notes)</label>
        <textarea
          data-testid="import-history-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={14}
          className="w-full px-4 py-3 rounded-2xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-y text-sm leading-relaxed font-mono"
          placeholder={"Visit 1 — 2025-03-14\nS: pt reports chronic upper back tension, headaches 2-3x/wk\nO: forward head, R upper trap hypertonic, ROM cervical rot R 45° / L 60°\nA: cervical postural strain w/ trap dysfunction\nP: trigger pt release UT bilat, ...\n\nVisit 2 — 2025-03-21\n..."}
        />
        <p className="text-xs text-ink-500 mt-2">Order doesn&apos;t matter — AI sorts sessions chronologically and rebuilds the timeline.</p>
      </div>
    </div>
  );
}

function JotformTab({ onClose }) {
  return (
    <div className="rounded-2xl bg-paper-50/60 border border-paper-200 p-6 text-center space-y-3">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-sage-500/15 flex items-center justify-center">
        <FileUp className="w-6 h-6 text-sage-700" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-xl font-medium text-ink-900">Jotform inbox</div>
      <p className="text-sm text-ink-700 max-w-md mx-auto">
        Connect your Jotform account once — every new intake submission lands in an inbox here, auto-matched to existing clients or queued as a new client draft.
      </p>
      <Link
        to="/integrations/jotform"
        onClick={onClose}
        data-testid="import-jotform-cta"
        className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-sm font-medium transition-all"
      >
        Open Jotform integration
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

// ---------- Draft review ----------

function DraftReview({ draft, meta, sessionDrafts, setDraftField, setDraftListAt, removeDraftListAt }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-sage-600" />
        <div className="label-overline">Draft — nothing saved yet</div>
        {meta?.confidence && (
          <span className={`ml-auto text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
            meta.confidence === "high" ? "bg-sage-100 text-sage-800" :
            meta.confidence === "medium" ? "bg-amber-100 text-amber-800" :
            "bg-rose-100 text-rose-800"
          }`}>{meta.confidence} confidence</span>
        )}
      </div>

      {meta?.extraction_notes && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <span className="font-medium">AI notes: </span>{meta.extraction_notes}
        </div>
      )}

      {/* Identity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name *" testid="draft-name" value={draft.name} onChange={(v) => setDraftField("name", v)} />
        <Field label="DOB (YYYY-MM-DD)" testid="draft-dob" value={draft.dob || ""} onChange={(v) => setDraftField("dob", v || null)} placeholder="1981-01-14" />
        <Field label="Email" testid="draft-email" value={draft.email || ""} onChange={(v) => setDraftField("email", v || null)} />
        <Field label="Phone" testid="draft-phone" value={draft.phone || ""} onChange={(v) => setDraftField("phone", v || null)} />
        <Field label="Occupation" testid="draft-occupation" value={draft.occupation || ""} onChange={(v) => setDraftField("occupation", v || null)} />
        <Field label="Emergency contact" testid="draft-emergency" value={draft.emergency_contact || ""} onChange={(v) => setDraftField("emergency_contact", v || null)} />
      </div>

      <ListField label="Chief complaints" items={draft.chief_complaints} testid="draft-complaints" onUpdate={(i, v) => setDraftListAt("chief_complaints", i, v)} onRemove={(i) => removeDraftListAt("chief_complaints", i)} />
      <ListField label="Medical history" items={draft.medical_history} testid="draft-medhx" onUpdate={(i, v) => setDraftListAt("medical_history", i, v)} onRemove={(i) => removeDraftListAt("medical_history", i)} />
      <ListField label="Sensitivities" items={draft.sensitivities} testid="draft-sensitivities" onUpdate={(i, v) => setDraftListAt("sensitivities", i, v)} onRemove={(i) => removeDraftListAt("sensitivities", i)} />
      <ListField label="Previous treatments" items={draft.previous_treatments} testid="draft-prev-tx" onUpdate={(i, v) => setDraftListAt("previous_treatments", i, v)} onRemove={(i) => removeDraftListAt("previous_treatments", i)} />

      {draft.long_term_goals?.length > 0 && (
        <ListField label="Long-term goals" items={draft.long_term_goals} testid="draft-goals" onUpdate={(i, v) => setDraftListAt("long_term_goals", i, v)} onRemove={(i) => removeDraftListAt("long_term_goals", i)} />
      )}
      {draft.clinical_patterns?.length > 0 && (
        <ListField label="Clinical patterns" items={draft.clinical_patterns} testid="draft-patterns" onUpdate={(i, v) => setDraftListAt("clinical_patterns", i, v)} onRemove={(i) => removeDraftListAt("clinical_patterns", i)} />
      )}

      {draft.intake_summary && (
        <div>
          <label className="label-overline block mb-2">AI intake summary</label>
          <textarea
            data-testid="draft-intake-summary"
            value={draft.intake_summary}
            onChange={(e) => setDraftField("intake_summary", e.target.value)}
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm resize-none"
          />
        </div>
      )}

      {/* Historical session drafts */}
      {sessionDrafts.length > 0 && (
        <div>
          <div className="label-overline mb-3 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" />
            {sessionDrafts.length} session{sessionDrafts.length === 1 ? "" : "s"} reconstructed
          </div>
          <div className="space-y-2" data-testid="draft-sessions">
            {sessionDrafts.map((s, i) => (
              <div key={s.id} className="rounded-xl bg-paper-50 border border-paper-200 p-3 text-sm">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <div className="font-medium text-ink-900">{s.imported_label || `Session ${i + 1}`}</div>
                  <div className="text-xs text-ink-500">
                    {s.created_at?.slice(0, 10) || "—"}
                  </div>
                </div>
                {s.chief_complaint && <div className="text-xs text-ink-700">CC: {s.chief_complaint}</div>}
                <div className="text-xs text-ink-500 mt-1 flex flex-wrap gap-x-3">
                  {s.outcome?.pain_before != null && <span>Pain {s.outcome.pain_before} → {s.outcome.pain_after ?? "—"}</span>}
                  {s.worked_areas?.length > 0 && <span>{s.worked_areas.length} muscle{s.worked_areas.length === 1 ? "" : "s"}</span>}
                  {s.outcome?.techniques_used?.length > 0 && <span>{s.outcome.techniques_used.length} technique{s.outcome.techniques_used.length === 1 ? "" : "s"}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {meta?.progress_summary && (meta.progress_summary.improved?.length || meta.progress_summary.worsened?.length || meta.progress_summary.unchanged?.length) > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="draft-progress-summary">
          <ProgressCol label="Improved" tone="sage" items={meta.progress_summary.improved} />
          <ProgressCol label="Unchanged" tone="amber" items={meta.progress_summary.unchanged} />
          <ProgressCol label="Worsened" tone="rose" items={meta.progress_summary.worsened} />
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="label-overline block mb-2">{label}</label>
      <input
        data-testid={testid}
        value={value || ""}
        placeholder={placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
      />
    </div>
  );
}

function ListField({ label, items, testid, onUpdate, onRemove }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <label className="label-overline block mb-2">{label}</label>
      <div className="space-y-1.5" data-testid={testid}>
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={typeof it === "string" ? it : JSON.stringify(it)}
              onChange={(e) => onUpdate(i, e.target.value)}
              className="flex-1 min-h-[40px] px-3 rounded-lg bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="w-10 h-10 rounded-lg hover:bg-rose-50 text-ink-500 hover:text-rose-700 flex items-center justify-center transition-colors"
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressCol({ label, tone, items }) {
  if (!items || items.length === 0) return null;
  const toneClass = {
    sage: "bg-sage-50 border-sage-200 text-sage-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  }[tone];
  return (
    <div className={`rounded-xl border p-3 text-xs ${toneClass}`}>
      <div className="font-semibold uppercase tracking-wider text-[10px] mb-1.5 opacity-80">{label}</div>
      <ul className="space-y-1 list-disc list-inside">
        {items.slice(0, 6).map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}
